import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import i18n from '@/i18n';

interface RatingSystemProps {
  menuItemId: string;
  branchId: string;
  menuItemName: string;
  allowMultipleRatings?: boolean;
  onRatingSubmitted?: () => void;
}

export const RatingSystem: React.FC<RatingSystemProps> = ({
  menuItemId,
  branchId,
  menuItemName,
  allowMultipleRatings = false,
  onRatingSubmitted
}) => {
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [hoveredStar, setHoveredStar] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get current language
  const currentLanguage = i18n.language || 'en';
  const isArabic = currentLanguage === 'ar';

  // Fallback translations for consistent display
  const translations = {
    yourRating: isArabic ? 'تقييمك' : 'Your Rating',
    yourNameOptional: isArabic ? 'اسمك (اختياري)' : 'Your Name (Optional)',
    commentOptional: isArabic ? 'تعليق (اختياري)' : 'Comment (Optional)',
    submitRating: isArabic ? 'إرسال التقييم' : 'Submit Rating',
    submitting: isArabic ? 'جار الإرسال...' : 'Submitting...',
    ratingRequired: isArabic ? 'التقييم مطلوب' : 'Rating Required',
    pleaseSelectRating: isArabic ? 'يرجى اختيار تقييم قبل الإرسال.' : 'Please select a rating before submitting.',
    thankYou: isArabic ? 'شكرًا لك!' : 'Thank you!',
    ratingSubmittedSuccess: isArabic ? 'تم إرسال تقييمك بنجاح.' : 'Your rating has been submitted successfully.',
    error: isArabic ? 'خطأ' : 'Error',
    submitFailed: isArabic ? 'فشل في إرسال التقييم. يرجى المحاولة مرة أخرى.' : 'Failed to submit rating. Please try again.',
    alreadyRated: isArabic ? 'تم التقييم بالفعل' : 'Already Rated',
    alreadyRatedMessage: isArabic ? 'لقد قمت بتقييم هذا العنصر بالفعل' : 'You have already rated this item',
    namePlaceholder: isArabic ? 'أدخل اسمك' : 'Enter your name',
    commentPlaceholder: isArabic ? 'أخبرنا عن تجربتك مع هذا العنصر...' : 'Tell us about your experience with this item...'
  };

  const getClientIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Error fetching IP:', error);
      return 'unknown';
    }
  };

  const checkExistingRating = async (ip: string): Promise<boolean> => {
    if (allowMultipleRatings) return false;

    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('id')
        .eq('menu_item_id', menuItemId)
        .eq('ip_address', ip)
        .eq('feedback_type', 'rating');

      if (error) throw error;
      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking existing rating:', error);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error(translations.ratingRequired, {
        description: translations.pleaseSelectRating
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const clientIP = await getClientIP();
      
      // Check for existing rating unless multiple ratings are allowed
      const hasExistingRating = await checkExistingRating(clientIP);
      
      if (hasExistingRating) {
        toast.error(translations.alreadyRated, {
          description: translations.alreadyRatedMessage
        });
        return;
      }

      const { error } = await supabase
        .from('feedback')
        .insert({
          menu_item_id: menuItemId,
          branch_id: branchId,
          feedback_type: 'rating',
          rating: rating,
          comment: comment.trim() || null,
          customer_name: customerName.trim() || null,
          ip_address: clientIP
        });

      if (error) {
        // Check if this is a duplicate rating error
        if (error.code === '23505' && error.message.includes('idx_feedback_unique_rating_per_ip')) {
          toast.error(translations.alreadyRated, {
            description: translations.alreadyRatedMessage
          });
          return;
        }
        throw error;
      }

      toast.success(translations.thankYou, {
        description: translations.ratingSubmittedSuccess
      });
      
      // Reset form
      setRating(0);
      setComment('');
      setCustomerName('');
      
      // Notify parent component
      if (onRatingSubmitted) {
        onRatingSubmitted();
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error(translations.error, {
        description: translations.submitFailed
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex gap-1 justify-center sm:justify-start">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-8 w-8 cursor-pointer transition-colors ${
              star <= (hoveredStar || rating)
                ? 'text-yellow-500 fill-current'
                : 'text-gray-300 hover:text-yellow-400'
            }`}
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(0)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4 p-1" dir={isArabic ? 'rtl' : 'ltr'}>
      <div>
        <Label className="text-sm font-medium block mb-2">{translations.yourRating}</Label>
        <div className="w-full">
          {renderStars()}
        </div>
      </div>

      <div>
        <Label htmlFor="customer-name" className="text-sm font-medium block mb-1">
          {translations.yourNameOptional}
        </Label>
        <Input
          id="customer-name"
          type="text"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          placeholder={translations.namePlaceholder}
          className="w-full"
          maxLength={100}
        />
      </div>

      <div>
        <Label htmlFor="comment" className="text-sm font-medium block mb-1">
          {translations.commentOptional}
        </Label>
        <Textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={translations.commentPlaceholder}
          className="w-full"
          rows={3}
          maxLength={500}
        />
      </div>

      <Button 
        onClick={handleSubmit} 
        disabled={isSubmitting || rating === 0}
        className="w-full"
      >
        {isSubmitting ? translations.submitting : translations.submitRating}
      </Button>
    </div>
  );
};
